import { z } from "zod";

// Looser than z.url() on purpose: stored values include bare domains like "mxschool.edu", and
// z.url() rejects anything without a scheme.
const webUrl = /^\S+\.\S{2,}$/;

// US ZIP or Canadian postal code -- both the State/Province and Country selects offer CA, so a
// US-only rule would make every Canadian entity unsavable.
const postalCode = /^(\d{5}(-\d{4})?|[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d)$/;

// employeeCount and callsPerMonth land in SQL Server int columns.
const SQL_INT_MAX = 2147483647;

// Field names match C# LegalEntitiesDTO (camelCase of PascalCase properties).
//
// Tier 1 fields block Save because a provider rejects the submission without them -- see DNC-415.
// Tier 2 (ein, dunsNumber, callsPerMonth) allow empty but validate any value that is present:
// there is no rejection risk in leaving them blank, only in filling them in wrong.
//
// Three rules from the ticket are intentionally NOT enforced here, because the submitter already
// handles them and adding them would only block legitimate input:
//   - "no & < >" in legalName: Utils.SanitizeForTns strips them, and stored names contain them.
//   - 200-char cap on the two narrative fields: only Hiya truncates, in BuildHiyaDescription, and
//     the full 4,000 characters are kept for everything else.
//   - employeeCount as a reference-data value: ResolveDemographics maps any integer to a range.
export const legalEntityFullSchema = z.object({
  legalName: z.string().min(1, "Legal entity name is required").max(255),
  ein: z
    .string()
    .regex(/^\d{2}-\d{7}$/, "EIN format: XX-XXXXXXX")
    .optional()
    .or(z.literal("")),
  streetAddress: z.string().min(1, "Address is required").max(255),
  city: z.string().min(1, "City is required").max(100),
  // Select-driven, and its option values are 2-letter codes: min(1) alone let a legacy "Texas"
  // pass while the control showed nothing selected, then wrote it straight back.
  state: z.string().regex(/^[A-Z]{2}$/, "State is required"),
  zip: z
    .string()
    .min(1, "Postal code is required")
    .regex(postalCode, "Postal code format: 12345, 12345-6789 or A1A 1A1"),
  countryCode: z
    .string()
    .regex(/^[A-Z]{2}$/, "Country is required (2-letter code)")
    .default("US"),
  industryId: z.string().min(1, "Industry is required"),
  primaryContactName: z.string().min(1, "Contact name is required").max(255),
  primaryContactEmail: z
    .string()
    .min(1, "Contact email is required")
    .email("Contact email is not a valid email address")
    .max(255),
  primaryContactPhoneNumber: z
    .string()
    .min(1, "Contact phone number is required")
    .regex(/^\(\d{3}\) \d{3}-\d{4}$/, "Contact phone number format: (XXX) XXX-XXXX"),
  url: z
    .string()
    .min(1, "Web URL is required")
    .max(255)
    .regex(webUrl, "Web URL must be a domain, e.g. example.com"),
  dunsNumber: z
    .string()
    .regex(/^\d{9}$/, "DUN number must be 9 digits")
    .optional()
    .or(z.literal("")),
  primaryPhoneNumber: z
    .string()
    .min(1, "Primary phone number is required")
    .regex(/^\(\d{3}\) \d{3}-\d{4}$/, "Primary phone number format: (XXX) XXX-XXXX"),
  // "" from an empty number input would coerce to 0, so map it to undefined before the range check.
  // Both counts are SQL int columns: without an upper bound an oversized value gets past Zod and
  // fails in JSON model binding instead, which surfaces as a generic 400 with no field named.
  callsPerMonth: z.preprocess(
    (v) => (v === "" || v === null ? undefined : v),
    z.coerce
      .number()
      .int()
      .min(1, "Calls per month must be a positive number")
      .max(SQL_INT_MAX, "Calls per month is too large")
      .optional()
  ),
  employeeCount: z.coerce
    .number()
    .int()
    .min(1, "Employee count is required")
    .max(SQL_INT_MAX, "Employee count is too large"),
  dba: z.string().max(255).optional().or(z.literal("")),
  licenceNumber: z.string().max(50).optional().or(z.literal("")),
  callPurposeDescription: z.string().max(4000).optional().or(z.literal("")),
  potentialNegativeReaction: z.string().max(4000).optional().or(z.literal("")),
  tnsEnterpriseId: z.string().max(50).optional().or(z.literal("")),
  isDefault: z.boolean().default(false),
});

export const emailSettingsSchema = z.object({
  emailAddress: z.string().min(1, "Email address is required"),
  emailFrequency: z.string().min(1, "Frequency is required"),
  scamScoreEmailFrequency: z.number().int().min(0),
});

export const webhookSettingsSchema = z.object({
  webhookUrl: z.string(),
  isActive: z.boolean(),
  notifyVerizon: z.boolean(),
  notifyATT: z.boolean(),
  notifyTMobile: z.boolean(),
  notifyOnFlagRemoved: z.boolean(),
});

export const adHocScanSchema = z.object({
  phoneNumbers: z.string().min(1, "Enter at least one phone number"),
});

export const originateCallSchema = z.object({
  destination: z.string().min(1, "Select a destination"),
  durationSeconds: z
    .number()
    .int()
    .min(1, "Minimum 1 second")
    .max(3600, "Maximum 3600 seconds"),
});

export const csvFileSchema = z.object({
  file: z
    .instanceof(File)
    .refine((f) => f.name.endsWith(".csv"), "File must be a CSV")
    .refine(
      (f) => f.size <= 10 * 1024 * 1024,
      "File must be under 10MB"
    ),
});

export const rescanCarriersSchema = z
  .object({
    scanVerizon: z.boolean(),
    scanATT: z.boolean(),
    scanTMobile: z.boolean(),
  })
  .refine(
    (data) => data.scanVerizon || data.scanATT || data.scanTMobile,
    "Select at least one carrier"
  );
